import {
    Grid,
    Typography
} from '@mui/material';
import { WithStyles, useTheme, withStyles } from '@mui/styles';
import { BasicButton } from '@tassat/button';
import { OrderSideChar, TransactionTypeChar, TransactionTypeEnum } from '@tassat/common-definitions';
import { ITransactFormValues } from '@tassat/forms/src/TransactForm/types';
import { Transaction } from '@tassat/fred-definitions';
import { TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import React from 'react';

import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

import { muiStyles } from './transactionStyles';
import { ISendFormValues } from '../../../containers/client/overlays/send/sendForm/types';
import { IWithdrawalFormValues } from '../../../containers/client/overlays/withdrawal/withdrawalForm/types';

interface TransactionFailedProps extends WithStyles<typeof muiStyles> {
    transactionValues:
        ISendFormValues | ITransactFormValues | IWithdrawalFormValues |
        TransactionHistoryRecord;
    onClose: () => void;
}

export function TransactionFailedComponent (props: TransactionFailedProps) {
    const {
        classes,
        transactionValues,
        onClose
    } = props;

    const theme = useTheme<ApplicationMuiTheme>();

    const {
        email = 'tassatpaysupport@tassatpay.com',
        phoneDirect,
        phoneTollFree
    } = theme?.app?.clientTheme.support.contactInfo || {};

    const fromApproval = (transactionValues as Transaction).RequestId;
    const transactionType = (() => {
        if (!transactionValues) return;

        if (fromApproval) {
            return (transactionValues as TransactionHistoryRecord).type === TransactionTypeChar.order ?
                (transactionValues as TransactionHistoryRecord).OrderSide
                : (transactionValues as TransactionHistoryRecord).type;
        } else {
            return (transactionValues as ITransactFormValues).Type;
        }
    })();

    const renderFailureText = () => {
        switch (transactionType) {
            case TransactionTypeEnum.send:
            case TransactionTypeChar.send:
            case TransactionTypeChar.transfer:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='h5'
                        >
                            Your transfer was unable to be processed.
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            Please check the details of your transaction, such as account information and amount, and try again,
                            or contact {email} or {phoneDirect || phoneTollFree || 'support line'} for assistance.
                        </Typography>
                    </>
                );
            case TransactionTypeEnum.withdrawal:
            case TransactionTypeChar.withdrawal:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='h5'
                        >
                            Your withdrawal was unable to be processed.
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            Please check the details of your transaction, such as account information and amount, and try again,
                            or contact {email} or {phoneDirect || phoneTollFree || 'support line'} for assistance.
                        </Typography>
                    </>
                );
            case TransactionTypeChar.order:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            {`Your ${OrderSideChar[transactionType]} was unable to be processed`}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            Please check the details of your transaction, such as account information and amount, and try again,
                            or contact {email} or {phoneDirect || phoneTollFree || 'support line'} for assistance.
                        </Typography>
                    </>
                );
            default:
                return (
                    <Typography
                        align='center'
                        className={classes.paddedText}
                        variant='body1'
                    >
                        Your transaction was unable to be processed.
                    </Typography>
                );
        }
    };

    if (!transactionValues && !(transactionValues as ITransactFormValues).Type) return <div />;

    return (
        <Grid
            alignItems='center'
            className={classes.root}
            container
            direction='column'
            spacing={0}
        >
            {renderFailureText()}
            <Grid
                className={classes.buttonContainer}
                container
                direction='row'
                item
                justifyContent='center'
                spacing={0}
            >
                <BasicButton
                    onClick={onClose}
                    text='DONE'
                    type='main'
                />
            </Grid>
        </Grid>
    );
}

const TransactionFailed = withStyles(muiStyles)(TransactionFailedComponent);
export { TransactionFailed };